import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Inject,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AppNotificationComponent,
    AppNotificationModel,
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    completedNotifications$,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigInterface,
    CONFIRMATION_TYPES,
    ConfirmationModel,
    ConfirmationSummaryModel,
    ConnectivityService,
    CSE_STOCK,
    EventService,
    isNgRxEnabled,
    ModalClass,
    NotificationIdentifier,
    NotificationIdentifierModel,
    NotificationModel,
    notificationRefreshRequired,
    NotificationStatusInstanceToken,
    TimesheetService,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { FormsService, FormsState, tryToPerformPaceFormsQuickAccess } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import {
    faBan,
    faCheckCircle,
    faEllipsisV,
    faExclamationTriangle,
    faFlag,
    faSync,
} from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { debounceTime, EMPTY, from, Observable, of, Subject, switchMap, timer } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, take, takeUntil, tap, throttleTime } from 'rxjs/operators';
import { EquipmentConfigurationComponent } from '../../details/equipment-configuration/equipment-configuration.component';
import EquipmentConfigurationModel from '../../details/equipment-configuration/equipment-configuration.model';
import { EquipmentHistoryComponent } from '../../details/equipment-history/equipment-history.component';
import EquipmentHistoryModel from '../../details/equipment-history/equipment-history.model';
import { ConfirmationSummaryComponent } from '../confirmation-summary/confirmation-summary.component';
import { ConfirmationDataInstanceToken, ConfirmationDataInterface } from '../interfaces/confirmation-data.interface';
import { ConfirmationCseStockComponent } from '../confirmation-cse-stock/confirmation-cse-stock.component';
import { MaterialStockService } from '../../material/services/material-stock-service';
import { ConfirmationReasonMissingProtocolComponent } from '../confirmation-reason-missing-protocol/confirmation-reason-missing-protocol.component';
import { toSignal } from '@angular/core/rxjs-interop';
import INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT = ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT;

const CLEAR_STATUS_INTERVAL = 3000;
const TIME_INTERVAL = 5000;

@Component({
    selector: 'pace-confirmation',
    templateUrl: './confirmation.component.html',
    styleUrls: ['./confirmation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ConfirmationComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly notificationStatusService = inject(NotificationStatusInstanceToken);

    public model: ConfirmationModel;
    public notification: NotificationModel;
    public confirmationStatus = ConfigConstants.SYNCING_STATUS;

    private readonly isShortConfirmation: boolean = false;

    public showFollowup = true;

    public showPaceFormsQuickAccess = false;
    public paceFormsQuickAccessTrigger$: Subject<void>;
    public destroyTrigger$: Subject<void>;

    private handoverWarningAcknowledged = false;

    public readonly faFlag = faFlag;
    public readonly faCheckCircle = faCheckCircle;
    public readonly faExclamationTriangle = faExclamationTriangle;
    public readonly faLoading = faSync;
    public readonly faBan = faBan;

    public confirmationGroup: FormGroup;
    public equipmentGroup: FormGroup;
    public equipmentChildren: FormArray;
    public complaintsGroup: FormGroup;
    public complaintsChildren: FormArray;
    public materialsGroup: FormGroup;
    public materialsChildren: FormArray;
    public measurementToolsGroup: FormGroup;
    public measurementToolsChildren: FormArray;
    public measuringPointsGroup: FormGroup;
    public measuringPointsChildren: FormArray;
    public serviceReportGroup: FormGroup;
    public serviceReportChildren: FormArray;
    public timeRecordingGroup: FormGroup;
    public timeRecordingChildren: FormArray;
    public expensesGroup: FormGroup;
    public expensesChildren: FormArray;
    public uploadToDocBoxGroup: FormGroup;
    public uploadToDocBoxChildren: FormArray;
    public participantsGroup: FormGroup;
    public participantsChildren: FormArray;
    public sourceReplacementsGroup: FormGroup;
    public sourceReplacementsChildren: FormArray;
    public followupGroup: FormGroup;
    public followupChildren: FormArray;
    public trainingEventCompletedGroup: FormGroup;
    public trainingEventCompletedChildren: FormArray;
    public installationPaperworkGroup: FormGroup;
    public installationPaperworkChildren: FormArray;

    private signConfirmation = new Subject<void>();
    private isSigningInProgress: boolean;
    private modalRef: BsModalRef;
    public isOnline$ = this.connectivityService.isOnline$;

    public strings: {
        uploadButtonLabel: string;
        downloadButtonLabel: string;
        saveButtonLabel: string;
        signButtonLabel: string;
        signLaterButtonLabel: string;
        submitButtonLabel: string;
        paceFormsQuickAccessLabel: string;
    };

    public uploadStatus: WritableSignal<ConfigConstants.SYNCING_STATUS> = signal(this.confirmationStatus.IDLE);
    public downloadStatus: WritableSignal<ConfigConstants.SYNCING_STATUS> = signal(this.confirmationStatus.IDLE);
    public saveRequired: WritableSignal<boolean> = signal(false);

    // Drives the installation paperwork & followUp accordion's invalid state reactively.
    public installationPaperworkInvalid: Signal<boolean>;
    public followupInvalid: Signal<boolean>;

    private resetStatusChangeSubject: Subject<void> = new Subject<void>();

    private readonly ngrxEnabled = isNgRxEnabled();
    private readonly completedNotifications$ = completedNotifications$();

    public constructor(
        private readonly store: Store<FormsState>,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly changeDetector: ChangeDetectorRef,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(ConfirmationDataInstanceToken) private readonly confirmationService: ConfirmationDataInterface,
        private readonly formsService: FormsService,
        private readonly appNotificationService: AppNotificationService,
        private readonly modalService: BsModalService,
        private readonly eventService: EventService,
        private readonly timesheetService: TimesheetService,
        private readonly formBuilder: FormBuilder,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly materialStockService: MaterialStockService,
        private readonly connectivityService: ConnectivityService,
    ) {
        this.model = <ConfirmationModel>this.activatedRoute.snapshot.data.confirmation;
        this.notification = <NotificationModel>this.activatedRoute.snapshot.data.notification;

        const routeCrm = activatedRoute.snapshot.paramMap.get('crmtype');
        const shortCrm: string = CONFIRMATION_TYPES.SHORT;
        this.isShortConfirmation = routeCrm === shortCrm;
        if (this.isShortConfirmation) {
            this.showFollowup = false;
        }

        this.strings = {
            downloadButtonLabel: <string>this.translate.instant('CONFIRMATION_DOWNLOAD_LABEL'),
            uploadButtonLabel: <string>this.translate.instant('CONFIRMATION_UPLOAD_LABEL'),
            saveButtonLabel: <string>this.translate.instant('CONFIRMATION_SAVE_LABEL'),
            signButtonLabel: <string>(
                this.translate.instant(this.isShortConfirmation ? 'CONFIRMATION_SEND_LABEL' : 'CONFIRMATION_SIGN_LABEL')
            ),
            signLaterButtonLabel: <string>this.translate.instant('CONFIRMATION_SIGN_LATER_LABEL'),
            submitButtonLabel: <string>this.translate.instant('CONFIRMATION_SUBMIT_LABEL'),
            paceFormsQuickAccessLabel: <string>this.translate.instant('CONFIRMATION_PACE_FORMS_LABEL'),
        };

        this.showPaceFormsQuickAccess = this.notification.isPaceFormsEnabled;

        this.destroyTrigger$ = new Subject<void>();

        const notification: NotificationIdentifier = {
            qmNum: this.notification.identifier.qmNum,
            maNum: this.notification.identifier.maNum,
            frontId: this.notification.identifier.frontId,
        };

        const mainEquipment = this.notification.getMainEquipment();
        if (typeof mainEquipment === 'undefined') {
            throw new Error(
                `ConfirmationComponent: constructor: Trying to open confirmation without main equipment for notification
                 ${this.notification.identifier.toString()}`,
            );
        }

        this.paceFormsQuickAccessTrigger$ = new Subject<void>();
        this.paceFormsQuickAccessTrigger$
            .pipe(throttleTime(ConfigConstants.debounceTimeMs), takeUntil(this.destroyTrigger$))
            .subscribe(() => {
                this.store.dispatch(
                    tryToPerformPaceFormsQuickAccess({
                        notification,
                        equipmentId: mainEquipment.equnr.value,
                        materialNr: mainEquipment.matnr.value,
                        serialNr: mainEquipment.sernr.value,
                    }),
                );
            });
        this.installationPaperworkChildren = this.formBuilder.array([]);
        // Drive the accordion's invalid state from the form status instead of reading `.invalid` directly in the
        // template. Dependent inputs (e.g. the installation paperwork radios/text-inputs) reassign their models
        // and re-validate deep in the component tree after this OnPush view has already been checked, so a plain
        // pull binding would only reflect the new validity on the next user interaction. Writing a signal on each
        // status change schedules change detection reliably (even under zone.js) and, unlike a synchronous
        // detectChanges() in the radio's valueChanges handler, does not interfere with radio-button rendering.
        this.installationPaperworkInvalid = this.updateSignalStatus(this.installationPaperworkChildren);
        this.followupChildren = this.formBuilder.array([]);
        this.followupInvalid = this.updateSignalStatus(this.followupChildren);
    }

    public ngOnInit(): void {
        this.isSigningInProgress = false;
        this.equipmentChildren = this.formBuilder.array([]);
        this.equipmentGroup = this.formBuilder.group({
            equipmentChildren: this.equipmentChildren,
        });
        this.complaintsChildren = this.formBuilder.array([]);
        this.complaintsGroup = this.formBuilder.group({
            complaintsChildren: this.complaintsChildren,
        });
        this.materialsChildren = this.formBuilder.array([]);
        this.materialsGroup = this.formBuilder.group({
            materialsChildren: this.materialsChildren,
        });
        this.measurementToolsChildren = this.formBuilder.array([]);
        this.measurementToolsGroup = this.formBuilder.group({
            measurementToolsChildren: this.measurementToolsChildren,
        });
        this.measuringPointsChildren = this.formBuilder.array([]);
        this.measuringPointsGroup = this.formBuilder.group({
            measuringPointsChildren: this.measuringPointsChildren,
        });
        this.serviceReportChildren = this.formBuilder.array([]);
        this.serviceReportGroup = this.formBuilder.group({
            serviceReportChildren: this.serviceReportChildren,
        });
        this.timeRecordingChildren = this.formBuilder.array([]);
        this.timeRecordingGroup = this.formBuilder.group({
            timeRecordingChildren: this.timeRecordingChildren,
        });
        this.expensesChildren = this.formBuilder.array([]);
        this.expensesGroup = this.formBuilder.group({
            expensesChildren: this.expensesChildren,
        });
        this.uploadToDocBoxChildren = this.formBuilder.array([]);
        this.uploadToDocBoxGroup = this.formBuilder.group({
            uploadToDocBoxChildren: this.uploadToDocBoxChildren,
        });
        this.participantsChildren = this.formBuilder.array([]);
        this.participantsGroup = this.formBuilder.group({
            participantsChildren: this.participantsChildren,
        });
        this.sourceReplacementsChildren = this.formBuilder.array([]);
        this.sourceReplacementsGroup = this.formBuilder.group({
            sourceReplacementsChildren: this.sourceReplacementsChildren,
        });
        this.followupGroup = this.formBuilder.group({
            followupChildren: this.followupChildren,
        });

        this.trainingEventCompletedChildren = this.formBuilder.array([]);
        this.trainingEventCompletedGroup = this.formBuilder.group({
            trainingEventChildren: this.trainingEventCompletedChildren,
        });
        this.installationPaperworkGroup = this.formBuilder.group({
            installationPaperworkChildren: this.installationPaperworkChildren,
        });

        this.confirmationGroup = this.formBuilder.group({
            equipment: this.equipmentGroup,
            complaints: this.complaintsGroup,
            materials: this.materialsGroup,
            measurementTools: this.measurementToolsGroup,
            measuringPoints: this.measuringPointsGroup,
            serviceReport: this.serviceReportGroup,
            timeRecording: this.timeRecordingGroup,
            expenses: this.expensesGroup,
            uploadToDocBox: this.uploadToDocBoxGroup,
            participants: this.participantsGroup,
            sourceReplacements: this.sourceReplacementsGroup,
            followup: this.followupGroup,
            trainingEventCompleted: this.trainingEventCompletedGroup,
            installationPaperwork: this.installationPaperworkGroup,
        });
        let materialFormStatusOld = ConfigConstants.formValid;
        this.materialsGroup.statusChanges.pipe(takeUntil(this.destroyTrigger$)).subscribe((statusNew: string) => {
            if (materialFormStatusOld !== statusNew) {
                materialFormStatusOld = statusNew;
                this.changeDetector.detectChanges();
            }
        });

        this.signConfirmation
            .asObservable()
            .pipe(
                exhaustMap(() => {
                    if (
                        this.notification.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO &&
                        this.configService.getShowBootOtherStockWarning(this.notification.taskGroup) &&
                        this.model?.materials &&
                        !this.model.materials?.listMaterial.value.some((material) =>
                            material.materialSource.isFromStock(),
                        )
                    ) {
                        const modalInstance = this.modalService.show(ConfirmationCseStockComponent, {
                            class: 'modal-md',
                            backdrop: 'static',
                            ariaLabelledBy: 'confirmation-cse-stock-modal-title',
                        });

                        return (modalInstance.content?.modalResult.asObservable() ?? of(false)).pipe(
                            mergeMap((result) => {
                                if (!result) {
                                    // If "No" is clicked, continue to summary screen
                                    return of(undefined);
                                } else if (result === CSE_STOCK.OTHER_STOCK) {
                                    // Open "Other Stock" modal and stop further execution
                                    this.openMaterialStock();
                                    return EMPTY;
                                }
                                return EMPTY;
                            }),
                        );
                    }

                    return of(undefined);
                }),
                exhaustMap(() =>
                    this.confirmationService.saveConfirmation(this.model).pipe(
                        mergeMap(() => this.checkTimesheetValidityForNormalEditableEntries()),
                        mergeMap(() =>
                            this.checkPaceFormsRequirements().pipe(
                                catchError((error) => {
                                    this.log.error(
                                        `ConfirmationComponent: sign: checkPaceFormsRequirements: ERROR:`,
                                        error,
                                    );
                                    return EMPTY;
                                }),
                            ),
                        ),
                        catchError((error) => {
                            this.log.error(
                                `ConfirmationComponent: sign: ERROR:
                                ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                            );
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('CONFIRMATION_SAVE_ERROR_TITLE'),
                                <string>this.translate.instant('CONFIRMATION_SAVE_ERROR_BODY'),
                            );
                            return EMPTY;
                        }),
                    ),
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(() => {
                this.saveRequired.set(false);
                this.isSigningInProgress = true;
                const confirmationSummaryModel = new ConfirmationSummaryModel(this.model);
                this.model.summary = confirmationSummaryModel;
                this.modalRef = this.modalService.show(ConfirmationSummaryComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_EXTRA_LARGE,
                    initialState: {
                        model: confirmationSummaryModel,
                    },
                    ariaLabelledBy: 'confirmation-summary-modal-title',
                });
                this.modalRef.onHide
                    ?.pipe(take(1), takeUntil(this.destroyTrigger$))
                    .subscribe(() => (this.isSigningInProgress = false));
            });

        // show question field in material if any question is answered as "YES"
        if (this.model.materials && this.model.complaints) {
            this.model.materials.isOptionYesSelectedInComplaints = this.model.complaints.showCommonQuestionnaire();
        }

        // Subscribe to the valueChanges of the parent form
        this.confirmationGroup.valueChanges
            .pipe(
                tap(() => {
                    this.saveRequired.set(true);
                }),
                debounceTime(TIME_INTERVAL), // Wait for 5 seconds after the last change
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(() => {
                this.save(); // Call the save method once after the debounce period
            });

        /**
         * Handle case where the confirmation is completed from a different device:
         * Navigate to dashboard and show a message in popup, that notification was already completed.
         */
        this.confirmationService.confirmationCompletedPreviously$
            .pipe(
                takeUntil(this.destroyTrigger$),
                tap(() => {
                    this.router.navigate([ConfigConstants.routeUrlDashboard], {}).catch((err: unknown) => {
                        this.log.error(
                            `[ConfirmationComponent] ngOnInit: Could not open Dashboard after checking completed notifications: ${JSON.stringify(err)}`,
                        );
                    });
                    this.requestNotificationRefresh();
                    if (this.modalRef) {
                        this.modalService.hide(this.modalRef.id);
                    }
                    this.modalService.show(YesNoModalDialogComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            model: new YesNoModalDialogModel(
                                <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_TITLE'),
                                <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_BODY', {
                                    notification: this.notification.identifier.toString(),
                                }),
                                <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_OK'),
                            ),
                        },
                        ariaLabelledBy: 'dashboard-button-modal-title',
                    });
                }),
            )
            .subscribe();

        /**
         * Listen to completed notifications. Trigger the 'confirmationCompletedFromOtherDevice$',
         * if the user navigates to a confirmation that was already completed.
         */
        this.completedNotifications$
            .pipe(
                takeUntil(this.destroyTrigger$),
                filter(() => !this.isSigningInProgress),
                filter(
                    (completedNotifications) =>
                        !!completedNotifications.completedNotifications[this.notification.identifier.toString()],
                ),
                mergeMap(() => this.confirmationService.confirmationCompletedPreviouslyCleanup(this.model)),
                tap(() => {
                    this.confirmationService.triggerConfirmationCompletedPreviously();
                }),
            )
            .subscribe();

        this.resetStatusChangeSubject
            .asObservable()
            .pipe(
                takeUntil(this.destroyTrigger$),
                switchMap(() => timer(CLEAR_STATUS_INTERVAL).pipe(take(1))),
                tap(() => {
                    this.uploadStatus.set(this.confirmationStatus.IDLE);
                    this.downloadStatus.set(this.confirmationStatus.IDLE);
                }),
            )
            .subscribe();
    }

    private openMaterialStock(): void {
        if (this.model.materials) {
            this.materialStockService.triggerMaterialStock(
                this.model.materials.notification,
                this.model.materials.listMaterial,
            );
        }
    }

    public ngAfterViewInit(): void {
        // the event trigger does not happen on loading data from local storage
        // hence we take this condition and implement it directly

        // set followup dependency on material's status/consumption
        if (this.model.materials && this.model.followup && this.model.materials.listMaterial.value.length > 0) {
            this.eventService.triggerMaterialConsumptionOrNotReceivedChanged(this.model.materials.listMaterial.value);
        }

        // set followup dependency on equipment's status after fix
        if (this.model.equipment && this.model.equipment.statusAfterFix.selectedEntry && this.model.followup) {
            this.eventService.triggerEquipmentStatusAfterFixChanged(this.model.equipment.statusAfterFix.selectedEntry);
        }

        if (this.model.equipment && this.model.materials && this.configService.getEnableBillingIndicator()) {
            this.eventService.triggerMaterialBillingIndicatorsNotReceived(this.model.materials.listMaterial.value);
        }

        // Since all the child forms for the accordions are added asynchronously, we now trigger a change detection
        // cycle asynchronously as well to update the "global" confirmation validation state
        Promise.resolve(undefined)
            .then(() => this.changeDetector.detectChanges())
            .catch((error) =>
                this.log.error(
                    `ConfirmationComponent: ngAfterViewInit: Error while triggering change detection `,
                    JSON.stringify(error, Object.getOwnPropertyNames(error)),
                ),
            );
    }

    /**
     * Asks for the notification list to be reloaded: a store dispatch once the notification domain
     * is served by the slice, and the `EventService` event the service behind
     * `NotificationDataInstanceToken` listens to while it is not.
     */
    private requestNotificationRefresh(): void {
        if (this.ngrxEnabled) {
            this.store.dispatch(notificationRefreshRequired());
            return;
        }
        this.eventService.triggerNotificationRefreshRequiredChanged(true);
    }

    public ngOnDestroy(): void {
        this.destroyTrigger$.next();
    }

    public navigateToPaceForms(): void {
        this.paceFormsQuickAccessTrigger$.next();
    }

    public save(): void {
        this.confirmationService
            .saveConfirmation(this.model)
            .pipe(
                take(1),
                catchError((error) => {
                    this.log.error(
                        'Error while saving confirmation: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CONFIRMATION_SAVE_ERROR_TITLE'),
                        <string>this.translate.instant('CONFIRMATION_SAVE_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => {
                this.saveRequired.set(false);
            });
    }

    public signLater(): void {
        this.confirmationService.signLater(this.notification);
        this.router
            .navigate([ConfigConstants.routeUrlDashboard])
            .catch((error: Error) =>
                this.log.error(
                    'Error in ConfirmationComponent -> signLater -> router.navigate:',
                    JSON.stringify(error, Object.getOwnPropertyNames(error)),
                ),
            );
    }

    private displayNoFormSubmitted(): Observable<unknown> {
        let modalInstance;
        if (
            this.configService.getDocMissingProtocolsEnabled() &&
            this.model.reasonMissingProtocols?.reasonForMissingProtocols?.value?.length
        ) {
            const initialState: ModalOptions = {
                initialState: {
                    model: this.model.reasonMissingProtocols,
                },
            };

            modalInstance = this.modalService.show(ConfirmationReasonMissingProtocolComponent, {
                class: ModalClass.MODAL_MEDIUM,
                keyboard: false,
                backdrop: 'static',
                ...initialState,
                ariaLabelledBy: 'confirmation-reason-missing-protocol',
            });
        } else {
            const bodyMessageKey = 'CONFIRMATION_SUMMARY_FORM_ERROR_F4M_BODY';

            const initialState = {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('CONFIRMATION_SUMMARY_FORM_WARN_F4M_TITLE'),
                    <string>this.translate.instant(bodyMessageKey),
                    <string>this.translate.instant('CONFIRMATION_SUMMARY_FORM_WARN_LABEL_YES'),
                    <string>this.translate.instant('CONFIRMATION_SUMMARY_FORM_WARN_LABEL_NO'),
                ),
            };
            modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                class: ModalClass.MODAL_MEDIUM,
                backdrop: 'static',
                initialState: initialState,
                ariaLabelledBy: 'yes-no-modal-title',
            });
        }

        const mainEquipment = this.notification.getMainEquipment();
        if (typeof mainEquipment === 'undefined') {
            throw new Error(`ConfirmationComponent: displayNoFormSubmitted: Notification has no main equipment!.`);
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore --- @TEO can you please check that?
        return modalInstance.content?.modalResult.asObservable().pipe(
            take(1),
            mergeMap((result) => {
                if (result) {
                    return of(undefined);
                } else {
                    this.store.dispatch(
                        tryToPerformPaceFormsQuickAccess({
                            notification: {
                                qmNum: this.notification.identifier.qmNum,
                                maNum: this.notification.identifier.maNum,
                                frontId: this.notification.identifier.frontId,
                            },
                            equipmentId: mainEquipment.equnr.value,
                            materialNr: mainEquipment.matnr.value,
                            serialNr: mainEquipment.sernr.value,
                        }),
                    );
                    throw new Error(
                        'ConfirmationComponent: displayNoFormSubmitted: No-form-submitted-warning not yet acknowledged.',
                    );
                }
            }),
        );
    }

    public checkPaceFormsRequirements(): Observable<unknown> {
        if (!this.notification.isPaceFormsEnabled) {
            return of(undefined);
        }

        return this.formsService.checkFormStatus(this.model.notificationIdentifier).pipe(
            mergeMap((warnings) => {
                if (warnings.openFormName.length > 0) {
                    // SHCNGMP58-723
                    // If a PACE Form was changed, a switch to confirmation summary screen should not be possible.
                    // Show a warning that the user has to submit, cancel or handover first.
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.WARN,
                        <string>this.translate.instant('CONFIRMATION_SUMMARY_FORM_ERROR_F4M_TITLE'),
                        <string>this.translate.instant(
                            'CONFIRMATION_SUMMARY_FORM_WARN_F4M_STARTEDBUTNOTSUBMITTED_BODY',
                            {
                                formName: warnings.openFormName,
                            },
                        ),
                    );
                    throw new Error(
                        'ConfirmationComponent: checkPaceFormsRequirements: Forms have been edited but not yet submitted.',
                    );
                } else if (warnings.handOverFormName.length > 0 && !this.handoverWarningAcknowledged) {
                    const type = AppNotificationType.WARN;
                    const titleText = <string>this.translate.instant('CONFIRMATION_SUMMARY_FORM_ERROR_F4M_TITLE');
                    const bodyText = <string>this.translate.instant('CONFIRMATION_SUMMARY_FORM_WARN_F4M_BODY', {
                        formName: warnings.handOverFormName,
                    });

                    const modalInstance = this.modalService.show(AppNotificationComponent, {
                        backdrop: 'static',
                        class: 'modal-md',
                        initialState: {
                            model: new AppNotificationModel(type, titleText, bodyText, () => {
                                this.handoverWarningAcknowledged = true;
                            }),
                        },
                        ariaLabelledBy: 'app-notification-modal-title',
                    });

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore --- @TEO can you please check that?
                    return modalInstance.content.modalResult$.pipe(
                        take(1),
                        tap(() => {
                            this.log.debug(`ConfirmationComponent: checkPaceFormsRequirements: Opening Summary Screen`);
                        }),
                        map(() => undefined),
                    );
                } else if (warnings.noFormSubmitted) {
                    // Skip the warning when all required IN15/IN30/IN31 forms or docbox uploads have final status.
                    if (this.model.installationPaperwork?.areAllInstallationPaperworkItemsInFinalStatus) {
                        return of(undefined);
                    }
                    return this.displayNoFormSubmitted();
                } else {
                    return of(undefined);
                }
            }),
        );
    }

    private displayInvalidTimesheetMessage(notificationId: NotificationIdentifierModel): Observable<void> {
        const timesheetModalInstance = this.modalService.show(YesNoModalDialogComponent, {
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('CONFIRMATION_SUMMARY_TIMESHEET_WARNING_TITLE'),
                    <string>this.translate.instant('CONFIRMATION_SUMMARY_TIMESHEET_WARNING_BODY', {
                        notification: `${notificationId.qmNum}:${notificationId.maNum}`,
                    }),
                    <string>this.translate.instant('CONFIRMATION_TO_TIMESHEET_BUTTON'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore --- @TEO can you please check that?
        return timesheetModalInstance.content?.modalResult.pipe(
            take(1),
            mergeMap((modalResult: boolean) => {
                if (modalResult) {
                    this.log.debug(
                        `ConfirmationComponent: displayInvalidTimesheetMessage: Sending the user to the timesheet.`,
                    );
                    timesheetModalInstance.hide();
                    return from(this.router.navigate([ConfigConstants.routeUrlTimesheet]));
                }
                return of(undefined);
            }),
            map(() => undefined),
            catchError((error) => {
                this.log.error(
                    'ConfirmationComponent: displayInvalidTimesheetMessage: Error in routing to timesheet:',
                    JSON.stringify(error),
                );
                return of(undefined);
            }),
        );
    }

    /**
     * Only for P58 excluding pace online because
     * other environments' timesheet editable entries are not affected by submitting another confirmation
     * If the timesheet's editable normal entries are not correct,
     * and is affected by current time-recording such that they become non-editable after this confirmation is submitted
     * then do not load the summary screen and show a warning that the user has to correct it first.
     *
     * @returns the observable of the timesheet validity check
     */
    public checkTimesheetValidityForNormalEditableEntries(): Observable<void> {
        if (!this.clientManager.isNgmP58()) {
            return of(undefined);
        }
        return this.timesheetService
            .isTimesheetInvalidForNormalEditableEntries(this.model.timeRecording.timeRecordingEntries.value)
            .pipe(
                mergeMap((result) => {
                    if (result.isInvalid) {
                        return this.displayInvalidTimesheetMessage(result.notificationId).pipe(
                            tap(() => {
                                throw new Error(
                                    `ConfirmationComponent: displayInvalidTimesheetMessage: ${result.validationErrorMessage}`,
                                );
                            }),
                        );
                    }
                    return of(undefined);
                }),
            );
    }

    public openEquipmentHistory(): void {
        this.modalService.show(EquipmentHistoryComponent, {
            backdrop: 'static',
            class: 'modal-lg modal-fullscreen-lg-down',
            initialState: {
                model: new EquipmentHistoryModel(this.notification),
            },
            ariaLabelledBy: 'equipment-history-modal-title',
        });
    }

    public openEquipmentConfig(): void {
        this.modalService.show(EquipmentConfigurationComponent, {
            backdrop: 'static',
            class: 'modal-lg modal-fullscreen-lg-down',
            initialState: {
                model: new EquipmentConfigurationModel(this.notification),
                notification: this.notification,
            },
            ariaLabelledBy: 'equipment-configuration-modal-title',
        });
    }

    public sign(): void {
        this.signConfirmation.next();
    }

    public debug(): void {
        this.log.debug('Outmodel: ', `${this.model.getOutModel()}`);
    }

    public notAllSectionsOpened(): boolean {
        let notAllSectionsOpened = false;
        for (const key of Object.keys(this.model.accordionOpenedStatus)) {
            // special handling for followup is needed, because
            // it is not displayed for Short confirmation but model will be created and data will be sent in background
            if (this.isShortConfirmation && key === 'followup') {
                continue;
            }
            if (this.model.hasOwnProperty(key) && !this.model.accordionOpenedStatus[key]) {
                notAllSectionsOpened = true;
                break;
            }
        }
        return notAllSectionsOpened;
    }

    public updateInstallationPaperworkFollowUp(shortText: INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT): void {
        this.model.followup?.setFollowUpForInstallationPaperwork(shortText);
        this.followupChildren.updateValueAndValidity();
    }

    protected readonly faEllipsisV = faEllipsisV;

    private updateSignalStatus(control: AbstractControl, initialValue = true): Signal<boolean> {
        return toSignal(control.statusChanges.pipe(map((status) => status === ConfigConstants.FORM_STATUS.INVALID)), {
            initialValue,
        });
    }
}
